import React from "react";
import { Button, ClassNameProps, Heading, If, makeClassName, Modal, ModalProps, StyleProps } from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon } from "../Icon";

const formStateArgs = { editMode: EditMode.WriteOnly };

type DetailFormModalControllerProps<T, TN extends string> = {
	heading?: JSX.Element | string | null;
	modalSize?: ModalProps["size"];
	modalClassName?: string;
	modalStyle?: React.CSSProperties;
	instanceName: TN;
	instance: T;
	DetailForm: (
		props: { [K in TN]: T } & { formStateArgs: { editMode: EditMode }; onSuccess: () => void },
	) => JSX.Element;
} & Partial<ClassNameProps & StyleProps>;

export function DetailFormModalController<T, TN extends string>({
	heading,
	instanceName,
	instance,
	DetailForm,
	modalSize,
	modalClassName,
	modalStyle,
	className,
	style,
}: DetailFormModalControllerProps<T, TN>) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const formProps = React.useMemo(
		() =>
			({
				[instanceName]: instance,
			} as { [K in TN]: T }),
		[instanceName, instance],
	);

	return (
		<React.Fragment>
			<Button variant="link" onClick={openModal} className={makeClassName("button--icon", className)} style={style}>
				<Icon.Gear size={16} className="block" />
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal size={modalSize} onClose={closeModal} className={modalClassName} style={modalStyle}>
					<If condition={!!heading}>
						<Modal.Header>
							<Heading.H2 noMargin>{heading}</Heading.H2>
						</Modal.Header>
					</If>
					<Modal.Body>
						<DetailForm {...formProps} formStateArgs={formStateArgs} onSuccess={closeModal} />
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}
